'use client';

import { SalesforcePlanPricingResponse } from '@/types/salesforce';
import { useCallback, useEffect, useState } from 'react';
import { useSortedMembers } from '../../../hooks/useSortedMembers';
import { useWizardContext } from '../../../hooks/useWizardContext';
import { useWizardNavigation } from '../../../hooks/useWizardNavigation';
import { calculatePlanPricing } from '../../../services/QuoteService';
import type { Member, Plan } from '../../../types';

export function useQuoteEditData() {
  const { state, dispatch } = useWizardContext();
  const { goToStep } = useWizardNavigation();
  const [pricing, setPricing] = useState<SalesforcePlanPricingResponse[]>([]);
  const [isEditingPlan, setIsEditingPlan] = useState(false);

  const members = state.formData.members || [];
  const sortedMembers = useSortedMembers(members);

  // Fetch quote on mount
  useEffect(() => {
    async function fetchQuote() {
      try {
        const result = await calculatePlanPricing(state.formData);
        setPricing(result);
      } catch (error) {
        console.error('Error fetching quote:', error);
      }
    }

    fetchQuote();
  }, []);

  const savePlanChanges = useCallback(
    (member: Member, plan: Plan) => {
      const updatedMembers = members.map(m =>
        m.id === member.id
          ? { ...m, plan, pediatrics_enabled: plan === 'Pediatrics' }
          : m
      );

      dispatch({
        type: 'SET_FORM_DATA',
        payload: {
          members: updatedMembers,
          additional_members: updatedMembers.length > 1,
          number_of_members: updatedMembers.length,
        },
      });
    },
    [members, dispatch]
  );

  const handleAddMembers = useCallback(() => {
    goToStep('quote-members');
  }, [goToStep]);

  const handleQuote = useCallback(() => {
    goToStep('quote');
  }, [goToStep]);

  return {
    members: sortedMembers,
    pricing,
    isEditingPlan,
    setIsEditingPlan,
    locationHasPediatrics: state.formData.location_has_pediatrics ?? false,
    savePlanChanges,
    handleAddMembers,
    handleQuote,
  };
}
